import { useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  Loader2,
  Upload,
  X,
} from "lucide-react";
import * as employeeApi from "../api";
import { useImportEmployees } from "../hooks";
import type { EmployeeImportSummary } from "../types";

export default function EmployeeImportModal({ onClose }: { onClose: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState<EmployeeImportSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const importMutation = useImportEmployees();

  async function handleDownloadTemplate() {
    setDownloading(true);
    try {
      const blob = await employeeApi.downloadImportTemplate();
      employeeApi.downloadBlob(blob, "employee-import-template.xlsx");
    } catch {
      setError("Failed to download the template. Please try again.");
    } finally {
      setDownloading(false);
    }
  }

  async function handleUpload() {
    if (!file) return;
    setError(null);
    setResult(null);
    try {
      const summary = await importMutation.mutateAsync(file);
      setResult(summary);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Import failed. Please check the file and try again.";
      setError(message);
    }
  }

  function reset() {
    setFile(null);
    setResult(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = "";
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-5 py-4">
          <h2 className="text-lg font-black text-slate-900">Import Employees</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          {!result && (
            <>
              <div className="rounded-xl border bg-slate-50 p-3">
                <p className="text-sm font-bold text-slate-800">Step 1 — Download template</p>
                <p className="mt-0.5 text-xs text-slate-500">
                  Fill in Employee Number, First Name, Last Name and Gender (required) plus any
                  optional fields, then upload it below.
                </p>
                <button
                  type="button"
                  onClick={handleDownloadTemplate}
                  disabled={downloading}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-xl border bg-white px-3 py-2 text-sm font-bold hover:bg-emerald-50 disabled:opacity-40"
                >
                  {downloading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 text-emerald-600" />
                  )}
                  Download Template
                </button>
              </div>

              <div className="rounded-xl border bg-slate-50 p-3">
                <p className="text-sm font-bold text-slate-800">Step 2 — Upload completed file</p>
                <div className="mt-2 flex items-center gap-2">
                  <input
                    ref={inputRef}
                    type="file"
                    accept=".xlsx"
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                    className="block w-full text-xs text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-200 file:px-3 file:py-1.5 file:text-xs file:font-bold file:text-slate-700 hover:file:bg-slate-300"
                  />
                </div>
                {file && (
                  <p className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-500">
                    <FileSpreadsheet className="h-3.5 w-3.5" />
                    {file.name}
                  </p>
                )}
              </div>

              {error && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </div>
              )}

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl border px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={!file || importMutation.isPending}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-700 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-800 disabled:opacity-40"
                >
                  {importMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  Upload &amp; Import
                </button>
              </div>
            </>
          )}

          {result && (
            <>
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-xl border bg-slate-50 px-3 py-2 text-center">
                  <p className="text-lg font-black text-slate-800">{result.totalRows}</p>
                  <p className="text-xs text-slate-500">Rows</p>
                </div>
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-center">
                  <p className="text-lg font-black text-emerald-700">{result.successCount}</p>
                  <p className="text-xs text-emerald-600">Created</p>
                </div>
                <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-center">
                  <p className="text-lg font-black text-red-700">{result.errorCount}</p>
                  <p className="text-xs text-red-600">Failed</p>
                </div>
              </div>

              {result.rows.length > 0 && (
                <div className="max-h-64 overflow-y-auto rounded-xl border">
                  <table className="min-w-full text-xs">
                    <thead className="sticky top-0 border-b bg-slate-50">
                      <tr className="text-left font-bold text-slate-500">
                        <th className="px-3 py-2">Row</th>
                        <th className="px-3 py-2">Emp No.</th>
                        <th className="px-3 py-2">Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.rows.map((row) => (
                        <tr key={row.rowNumber} className="border-t">
                          <td className="px-3 py-1.5 text-slate-500">{row.rowNumber}</td>
                          <td className="px-3 py-1.5 font-semibold text-slate-700">
                            {row.employeeNumber || "—"}
                          </td>
                          <td className="px-3 py-1.5">
                            <div
                              className={`flex items-center gap-1.5 ${
                                row.success ? "text-emerald-700" : "text-red-700"
                              }`}
                            >
                              {row.success ? (
                                <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                              ) : (
                                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                              )}
                              {row.message}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={reset}
                  className="rounded-xl border px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50"
                >
                  Import Another File
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-800"
                >
                  Done
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
